/**
 * SuddenDeathManager - Manages sudden death activation and water level rising
 *
 * Extracted from game.ts as part of Phase 5 refactoring.
 * Uses callback pattern to keep state (currentWaterLevel) in game.ts
 * while delegating the logic to this manager.
 */

interface Worm {
  state: string;
  health: number;
  position: { x: number; y: number };
}

interface Team {
  worms: Worm[];
}

export interface SuddenDeathSettings {
  enabled: boolean;
  turnsBeforeStart: number;
  waterRisePerTurn: number;
}

export interface SuddenDeathManagerDeps {
  settings: SuddenDeathSettings;
  getTeams: () => Team[];
  getWaterLevel: () => number;
  setWaterLevel: (level: number) => void;
  maxWaterLevel: number;
  playSound: (sound: string) => void;
  showCommentary: () => void;
  showDamageNumber: (x: number, y: number, damage: number) => void;
  updateWaterVisual: () => void;
  shouldShowDamageNumbers: () => boolean;
}

export class SuddenDeathManager {
  private deps: SuddenDeathManagerDeps;
  private active = false;

  constructor(deps: SuddenDeathManagerDeps) {
    this.deps = deps;
  }

  checkSuddenDeath(totalTurnCount: number): void {
    const { settings } = this.deps;
    if (!settings.enabled) return;

    if (!this.active && totalTurnCount >= settings.turnsBeforeStart) {
      this.activateSuddenDeath();
    } else if (this.active) {
      this.raiseWaterLevel();
    }
  }

  activateSuddenDeath(): void {
    this.active = true;

    this.deps.playSound('suddenDeath');
    this.deps.showCommentary();

    for (const team of this.deps.getTeams()) {
      for (const worm of team.worms) {
        if (worm.state !== 'dead' && worm.health > 1) {
          const lostHealth = worm.health - 1;
          worm.health = 1;

          if (this.deps.shouldShowDamageNumbers()) {
            this.deps.showDamageNumber(worm.position.x, worm.position.y, lostHealth);
          }
        }
      }
    }
  }

  raiseWaterLevel(): void {
    const { settings, maxWaterLevel } = this.deps;
    const currentLevel = this.deps.getWaterLevel();

    const newLevel = Math.min(currentLevel + settings.waterRisePerTurn, maxWaterLevel);

    this.deps.setWaterLevel(newLevel);
    this.deps.updateWaterVisual();
  }

  isActive(): boolean {
    return this.active;
  }

  setActive(value: boolean): void {
    this.active = value;
  }

  reset(): void {
    this.active = false;
  }
}
